import React, { useEffect, useMemo, useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { CA_COUNTIES } from '@lds-efsp/shared-contracts';
import { FormSelect } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import { countySlug } from '../lib/county-slug';
import { resetCourtScopedCaseData } from '../lib/reset-court-scoped-case';
import { useApi, API_BASE_URL } from '../../../api/useApi';

const API_BASE = API_BASE_URL;

interface EFilingCounty {
  county: string;
  courtCount: number;
}

// Static fallback when the location engine is unavailable (mirrors OrderInfoTab)
const FALLBACK_COUNTIES: EFilingCounty[] = CA_COUNTIES.map(c => ({
  county: c.name,
  courtCount: 0,
}));

const stateOptions = [
  { value: '', label: 'Select state...' },
  { value: 'CA', label: 'California' },
];

interface InlineCourtSelectorProps {
  brand: BrandConfig;
}

/**
 * Step-1 court picker (Walter 2026-07-10 R14–R17), rendered INSIDE the "Court"
 * card on the Upload step. The court is the root of the document-typing
 * dependency chain — county → case categories/types → document types — but it
 * used to be collected two steps later (Where to File), so every filing whose
 * court OCR couldn't read forced a forward-then-back walk: ahead to set the
 * court and case type, back to type the documents. Setting the court right
 * here makes the Upload step self-sufficient: the whole chain completes in one
 * top-to-bottom pass, strictly forward. Mirrors OrderInfoTab's counties fetch
 * and writes to the same `orderInfo` slice, so the Where to File step later
 * shows the same selection.
 */
const InlineCourtSelector: React.FC<InlineCourtSelectorProps> = ({ brand }) => {
  const {
    wizardState,
    updateOrderInfo,
    updateCaseInfo,
    setParties,
    setAttorneys,
    setDocuments,
    confirmFlowEnabled,
    resetSectionStatuses,
  } = useEFiling();
  const { orderInfo } = wizardState;

  // SPEC-SOR-174 R17: the confirm-flow half of the court-change wipe (roster
  // whatever its source, every document's type + metadata with the files kept,
  // every guided-section status and filer confirm act). Built once here so the
  // two handlers below cannot drift — the SOR-162 single-chokepoint rule.
  const resetCourtScopedCase = (nextCourt: { state?: string; county?: string }) =>
    resetCourtScopedCaseData({
      caseTrackingId: wizardState.caseInfo.caseTrackingId,
      updateCaseInfo,
      setParties,
      setAttorneys,
      confirmFlowEnabled,
      documents: wizardState.documents,
      setDocuments,
      resetSectionStatuses,
      // R17's antecedent, stated by the handler that owns the transition — the
      // helper checks it rather than assuming a court change from the fact that
      // it was called (a same-value write must wipe nothing).
      previousCourt: { state: orderInfo.state, county: orderInfo.county },
      nextCourt,
    });
  const { fetchWithAuth } = useApi();

  const [counties, setCounties] = useState<EFilingCounty[]>([]);
  const [loadingCourts, setLoadingCourts] = useState(false);

  // Fetch eFiling-capable counties from the location engine (mirrors OrderInfoTab)
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- synchronous reset + loading flag before async fetch (mirrors OrderInfoTab) */
  useEffect(() => {
    if (!orderInfo.state) {
      setCounties([]);
      return;
    }

    setLoadingCourts(true);
    fetchWithAuth(`${API_BASE}/api/efiling/courts?state=${orderInfo.state}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        const data = json.data || [];
        setCounties(data.length > 0 ? data : FALLBACK_COUNTIES);
      })
      .catch(() => {
        setCounties(FALLBACK_COUNTIES);
      })
      .finally(() => setLoadingCourts(false));
  }, [orderInfo.state, fetchWithAuth]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // R85 (mirrors OrderInfoTab): an OCR-written county slug ('los-angeles')
  // matches the option values ('Los Angeles') only up to slugification, which
  // renders the select as empty. Canonicalize to the option value once
  // counties load — same-county rewrite, so the WOL-341 jurisdiction reset
  // must NOT fire.
  useEffect(() => {
    if (!orderInfo.county || counties.length === 0) return;
    if (counties.some(c => c.county === orderInfo.county)) return;
    const matched = counties.find(
      c => countySlug(c.county) === countySlug(orderInfo.county),
    );
    if (matched) updateOrderInfo({ county: matched.county });
  }, [counties, orderInfo.county, updateOrderInfo]);

  const countyOptions = useMemo(() => [
    { value: '', label: loadingCourts ? 'Loading counties...' : 'Select county...' },
    ...counties.map(c => ({
      value: c.county,
      label: c.courtCount > 0
        ? `${c.county} (${c.courtCount} court${c.courtCount !== 1 ? 's' : ''})`
        : c.county,
    })),
  ], [counties, loadingCourts]);

  // WOL-341: courtJurisdiction (in caseInfo) is a county-scoped sub-location.
  // Changing the state or county invalidates any previously-chosen courthouse,
  // so clear it here too. SOR-162: a change here must ALSO wipe an attached
  // existing case + its imported roster with the SAME semantics as the SOR-154
  // Where-to-File change — via the shared helper, not a second copy. When no
  // case is attached the helper clears only courtJurisdiction, preserving the
  // prior WOL-341 behavior. Clearing the case here also closes the Next-click
  // race (SOR-162 #2): the county update and the case wipe land in one handler,
  // so there is no render where the county moved but a stale `caseSourceCounty`
  // still points at the old court.
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateOrderInfo({ state: e.target.value, county: '' });
    resetCourtScopedCase({ state: e.target.value, county: '' });
  };

  const handleCountyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateOrderInfo({ county: e.target.value });
    resetCourtScopedCase({ state: orderInfo.state, county: e.target.value });
  };

  return (
    <div id="inline-court" data-testid="inline-court-selector">
      <p className="text-xs mb-3" style={{ color: brand.colors.textLight }}>
        {orderInfo.county
          ? 'Filing to the court below — change it here if needed.'
          : "We couldn't read the court from your document. Pick it here — case types and document types are court-specific — no need to leave this step."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormSelect
          brand={brand}
          label="State"
          name="inlineState"
          value={orderInfo.state}
          onChange={handleStateChange}
          options={stateOptions}
        />
        <FormSelect
          brand={brand}
          label="County"
          name="inlineCounty"
          value={orderInfo.county}
          onChange={handleCountyChange}
          options={countyOptions}
          disabled={!orderInfo.state || loadingCourts}
        />
      </div>
    </div>
  );
};

export default InlineCourtSelector;
